import type { ReactNode } from 'react';

export interface CrumbItem {
  label: string;
  href?: string;
  /** Для accessibility — пометить текущую страницу как aria-current="page". */
  current?: boolean;
}

interface Props {
  items: CrumbItem[];
  /** Разделитель между элементами. По умолчанию — `›`. */
  separator?: ReactNode;
}

/**
 * Breadcrumbs (хлебные крошки). Семантически корректные:
 *  - <nav aria-label="Навигация по разделам">
 *  - <ol> с явным порядком
 *  - последний элемент с aria-current="page" (или явно через item.current)
 *
 * Long names сжимаются через CSS overflow при overflow-wrap. На mobile
 * (<480px) поджимаются скроллом по горизонтали.
 */
export function Crumbs({ items, separator = '›' }: Props) {
  if (!items.length) return null;
  return (
    <nav aria-label="Навигация по разделам" className="crumbs">
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 6,
          listStyle: 'none',
          margin: 0,
          padding: 0,
          fontSize: 13,
          color: 'var(--muted, #6b7287)',
          overflowWrap: 'anywhere',
        }}
      >
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const isCurrent = item.current || isLast;
          return (
            <li
              key={`${i}-${item.label}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {item.href && !isCurrent ? (
                <a
                  href={item.href}
                  style={{
                    color: 'var(--navy-500, #1a2d5e)',
                    textDecoration: 'none',
                    borderBottom: '1px solid transparent',
                  }}
                  onMouseEnter={(e) =>
                    ((e.target as HTMLAnchorElement).style.borderBottomColor =
                      'var(--navy-500, #1a2d5e)')
                  }
                  onMouseLeave={(e) =>
                    ((e.target as HTMLAnchorElement).style.borderBottomColor = 'transparent')
                  }
                >
                  {item.label}
                </a>
              ) : (
                <span
                  aria-current={isCurrent ? 'page' : undefined}
                  style={{ color: isCurrent ? 'var(--text, #0e1320)' : 'inherit' }}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <span aria-hidden="true" style={{ color: 'var(--border, #e6e2d6)' }}>
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
